// Installs the client-side backend shim:
//   1. axios adapter  — intercepts the app's axios /api/* calls
//   2. fetch override — intercepts the app's raw fetch() /api/* calls
//   3. service worker — serves /clips,/uploads,/logos media to native tags
// Everything heavy (download bridge, Whisper, Gemini, WebCodecs render) runs in-page.

import axios from 'axios';
import { routeApi, NOT_HANDLED } from './routes.js';
import { getResponse } from './mediaStore.js';

// Page-cached media/artifact paths. We serve GETs for these straight from the in-page
// Cache Storage (not via the service worker) so the analysis JSON fetch can't race the
// SW lifecycle and get the SPA's index.html (which crashed the render step).
const STORE_PREFIXES = ['/clips/', '/uploads/', '/logos/', '/db/', '/transcripts/'];
const isStorePath = (p) => STORE_PREFIXES.some((x) => p.startsWith(x));

function pathOf(url) {
  try { return new URL(url, location.origin).pathname; } catch { return url; }
}
function lowerHeaders(h) {
  const out = {};
  if (!h) return out;
  const entries = typeof h.toJSON === 'function' ? Object.entries(h.toJSON()) : Object.entries(h);
  for (const [k, v] of entries) out[String(k).toLowerCase()] = v;
  return out;
}
function parseBody(data) {
  if (data == null) return {};
  if (typeof data === 'string') { try { return JSON.parse(data); } catch { return {}; } }
  return data;
}
function fileFromForm(form) {
  if (!(form instanceof FormData)) return null;
  for (const v of form.values()) if (v instanceof File) return v;
  return null;
}

// ── 1. axios adapter ──────────────────────────────────────────────────────────────
function installAxios() {
  const xhr = axios.getAdapter('xhr');
  axios.defaults.adapter = async (config) => {
    const path = pathOf(config.url || '');
    if (path.startsWith('/api/')) {
      const method = (config.method || 'get').toLowerCase();
      const isForm = config.data instanceof FormData;
      const req = {
        method, path,
        body: isForm ? {} : parseBody(config.data),
        file: isForm ? fileFromForm(config.data) : null,
        headers: lowerHeaders(config.headers),
        params: config.params || {},
      };
      const res = await routeApi(req);
      if (res !== NOT_HANDLED) {
        const status = res.status || 200;
        const response = {
          data: res.data, status, statusText: '', headers: res.headers || {},
          config, request: {},
        };
        const validate = config.validateStatus || ((s) => s >= 200 && s < 300);
        if (validate(status)) return response;
        const err = new Error(`Request failed with status code ${status}`);
        err.response = response; err.config = config; err.isAxiosError = true;
        throw err;
      }
    }
    // Serve cached artifacts/media straight from the store (no SW dependency).
    if ((config.method || 'get').toLowerCase() === 'get' && isStorePath(path)) {
      const stored = await getResponse(path);
      if (stored) {
        const ct = stored.headers.get('content-type') || '';
        const wantBlob = config.responseType === 'blob' || config.responseType === 'arraybuffer';
        const data = !wantBlob && ct.includes('json') ? await stored.clone().json()
          : config.responseType === 'arraybuffer' ? await stored.clone().arrayBuffer()
          : await stored.clone().blob();
        return { data, status: 200, statusText: 'OK', headers: { 'content-type': ct }, config, request: {} };
      }
    }
    return xhr(config); // delegate (XHR → service worker for /clips etc.)
  };
}

// ── 2. fetch override ───────────────────────────────────────────────────────────────
function installFetch() {
  const orig = window.fetch.bind(window);
  window.fetch = async (input, init = {}) => {
    const url = typeof input === 'string' ? input : input?.url;
    const path = pathOf(url || '');
    if (path.startsWith('/api/')) {
      const method = (init.method || (typeof input !== 'string' && input?.method) || 'get').toLowerCase();
      const isForm = init.body instanceof FormData;
      const req = {
        method, path,
        body: isForm ? {} : parseBody(init.body),
        file: isForm ? fileFromForm(init.body) : null,
        headers: lowerHeaders(init.headers),
        params: Object.fromEntries(new URL(url, location.origin).searchParams),
      };
      const res = await routeApi(req);
      if (res !== NOT_HANDLED) {
        return new Response(JSON.stringify(res.data), {
          status: res.status || 200,
          headers: { 'Content-Type': 'application/json' },
        });
      }
    }
    // Serve cached artifacts/media straight from the store (no SW dependency).
    if ((init.method || 'get').toLowerCase() === 'get' && isStorePath(path)) {
      const stored = await getResponse(path);
      if (stored) return stored.clone();
    }
    return orig(input, init);
  };
}

// ── 3. service worker (media server) ────────────────────────────────────────────────
async function installServiceWorker() {
  if (!('serviceWorker' in navigator)) return;
  try {
    await navigator.serviceWorker.register('/sw.js');
    await navigator.serviceWorker.ready;
  } catch (e) {
    console.warn('[shim] service worker registration failed:', e);
  }
}

// The app opens a log WebSocket to the desktop backend (ws://host:3001/ws/logs) which
// doesn't exist here. Stub those connections so they don't spam the console with
// ERR_CONNECTION_REFUSED. Any other WebSocket use passes through untouched.
function installWebSocketStub() {
  const Real = window.WebSocket;
  function Stub(url, protocols) {
    if (typeof url === 'string' && /\/ws\/logs/.test(url)) {
      // a dead, silent socket: never opens, never errors
      this.url = url; this.readyState = 3; // CLOSED
      this.send = () => {}; this.close = () => {};
      this.addEventListener = () => {}; this.removeEventListener = () => {};
      return;
    }
    return new Real(url, protocols);
  }
  Stub.prototype = Real.prototype;
  Stub.CONNECTING = 0; Stub.OPEN = 1; Stub.CLOSING = 2; Stub.CLOSED = 3;
  window.WebSocket = Stub;
}

// ?demo=1 enables the keyless offline preview (mock download + canned analysis + real
// render); ?demo=0 turns it back off. Lets the live app be tried end-to-end with no
// bridge and no Gemini key. Real mode (real YouTube + real key) is the default.
function applyDemoParam() {
  try {
    const p = new URLSearchParams(location.search);
    if (p.get('demo') === '1') {
      localStorage.setItem('qlipper-web:demo', '1');
      localStorage.setItem('qlipper-web:bridge', 'mock');
      console.log('[shim] DEMO MODE on (keyless preview).');
    } else if (p.get('demo') === '0') {
      localStorage.removeItem('qlipper-web:demo');
      localStorage.removeItem('qlipper-web:bridge');
      console.log('[shim] demo mode off (real bridge + key).');
    }
  } catch { /* ignore */ }
}

let _installed = false;
export async function installShim() {
  if (_installed) return;
  _installed = true;
  applyDemoParam();
  installAxios();
  installFetch();
  installWebSocketStub();
  await installServiceWorker();
  window.__SHIM_OK__ = true;
  console.log('[shim] installed — backend running client-side');
}
